import { IVacancy, IWage, IWorkplaceDetails, LocalizationData, LocalizationPath, VacancyGetters } from '@tokyojob/core'
import { IconSize, IconType, LabelFactory } from '@tokyojob/frontend-components'
import { LocalizationService, VacancyCardText, WageCardText } from '@tokyojob/frontend-core'
import { VacancyDetailCardTitle } from '../getters/vacancy-detail-card-title'
import { IVacancyDetailCard } from '../interface/i-vacancy-detail-card'
import { IVacancyDetailCardSubcard } from '../interface/i-vacancy-detail-card-subcard'
import { IVacancyDetailCardSubcardRowItem } from '../interface/i-vacancy-detail-card-subcard-row-item'

const wide = true
/**
 * @todo Tests
 */
export class VacancyDetailCardFactory {
  /**
   * Create the 'Contract Details' Card used in the Vacancy Details Page
   * @param IWage
   * @returns a formatted IVacancyDetailCard
   */
  static contractDetails(localization: LocalizationData, vacancy: IVacancy): IVacancyDetailCard {
    const title = VacancyDetailCardTitle.ContractDetails(localization)
    const subcard: IVacancyDetailCardSubcard = { rowItemsArray: [] }
    const contractType = VacancyGetters.getContractType(vacancy)
    const employmentPeriod = VacancyCardText.getEmployementPeriod(localization, vacancy)
    const trialPeriod = VacancyCardText.getTrialPeriod(localization, vacancy)
    if (contractType) {
      subcard.rowItemsArray.push({
        title: LocalizationService.t<string>(localization, LocalizationPath.ContractType),
        labels: [LabelFactory.new({ label: [contractType] })]
      })
    }
    if (employmentPeriod) {
      subcard.rowItemsArray.push({
        title: LocalizationService.t<string>(localization, LocalizationPath.EmploymentPeriod),
        labels: [LabelFactory.new({ label: [employmentPeriod] })]
      })
    }
    if (trialPeriod) {
      subcard.rowItemsArray.push({
        title: LocalizationService.t<string>(localization, LocalizationPath.ProbationPeriod),
        labels: [LabelFactory.new({ label: [trialPeriod] })]
      })
    }
    return {
      title: title,
      subcards: VacancyDetailCardFactory.vacancyDetailCardFormat(localization, title, [subcard])
    }
  }

  /**
   * Create 'Salary' row
   */
  static newSalarySubcard(
    localization: LocalizationData,
    wage: IWage,
    hasPerformanceBonus: boolean
  ): IVacancyDetailCardSubcardRowItem | undefined {
    const salary = WageCardText.getFormattedWage(localization, wage)
    if (!salary) return undefined
    return {
      title: LocalizationService.t<string>(localization, LocalizationPath.Salary),
      labels: [
        LabelFactory.new({
          label: [
            salary + (hasPerformanceBonus ? LocalizationService.t<string>(localization, LocalizationPath.performanceBonus) : '')
          ],
          icon: { icon: 'yen-sign', type: IconType.duotone, size: IconSize.small }
        })
      ]
    }
  }

  /**
   * Create 'Daily shift' Row
   */
  static newShiftSubcard(localization: LocalizationData, wage: IWage): IVacancyDetailCardSubcardRowItem | undefined {
    const shiftTimes = []
    const shiftFrom = WageCardText.getShiftFrom(localization, wage)
    const shiftUntil = WageCardText.getShiftUntil(localization, wage)
    if (shiftFrom) shiftTimes.push(LabelFactory.new({ label: [shiftFrom] }))
    if (shiftUntil) shiftTimes.push(LabelFactory.new({ label: [shiftUntil] }))
    if (shiftTimes.length === 0) return undefined
    return {
      title: LocalizationService.t<string>(localization, LocalizationPath.DailyShift),
      labels: shiftTimes
    }
  }

  /**
   * Create 'Salary Closing Day' row
   */
  static newSalaryClosingDaySubcard(localization: LocalizationData, wage: IWage): IVacancyDetailCardSubcardRowItem | undefined {
    const salaryClosingDay = WageCardText.getSalaryClosingDay(localization, wage)
    if (!salaryClosingDay) return undefined
    return {
      title: LocalizationService.t<string>(localization, LocalizationPath.SalaryClosingDay),
      labels: [LabelFactory.new({ label: [salaryClosingDay] })]
    }
  }

  /**
   * Create 'Working days' row
   */
  static newWorkingDaysSubcard(localization: LocalizationData, wage: IWage): IVacancyDetailCardSubcardRowItem | undefined {
    const workingDays = WageCardText.getWorkingDays(localization, wage)
    if (!workingDays) return undefined

    const formattedWorkingDays = []
    for (const day of workingDays) formattedWorkingDays.push(LabelFactory.new({ label: [day] }))
    return {
      title: LocalizationService.t<string>(localization, LocalizationPath.WorkingDays),
      labels: formattedWorkingDays
    }
  }

  /**
   * Create 'Possible amount of overtime' row
   */
  static newPossibleAmountOfOvertimeSubcard(
    localization: LocalizationData,
    wage: IWage
  ): IVacancyDetailCardSubcardRowItem | undefined {
    const possibleAmountOfOvertime = WageCardText.getPossibleOvertime(localization, wage)
    if (!possibleAmountOfOvertime) return undefined
    return {
      title: LocalizationService.t<string>(localization, LocalizationPath.PossibleOvertime),
      labels: [LabelFactory.new({ label: [possibleAmountOfOvertime] })]
    }
  }

  /**
   * Create 'Payday' row
   */
  static newSalaryPaymentDaySubcard(localization: LocalizationData, wage: IWage): IVacancyDetailCardSubcardRowItem | undefined {
    const salaryPaymentDay = WageCardText.getSalaryPaymentDay(localization, wage)
    if (!salaryPaymentDay) return undefined
    return {
      title: LocalizationService.t<string>(localization, LocalizationPath.Payday),
      labels: [LabelFactory.new({ label: [salaryPaymentDay] })]
    }
  }

  /**
   * @param vacancy
   * @returns
   */
  static newWagesIncentivesTitle(localization: LocalizationData, vacancy: IVacancy): string {
    let secondTitle = ''
    if (vacancy.otherSalariesAndBonuses && VacancyGetters.getOtherSalariesAndBonusesDetails(vacancy)) {
      secondTitle = LocalizationService.t<string>(localization, LocalizationPath.SalaryIncentivesAndPerformanceBonus)
    }
    return secondTitle
  }

  /**
   * @param vacancy
   * @returns
   */
  static newWagesIncentivesDescription(vacancy: IVacancy): string {
    let secondTitleDescription = ''
    if (vacancy.otherSalariesAndBonuses && VacancyGetters.getOtherSalariesAndBonusesDetails(vacancy)) {
      secondTitleDescription = VacancyGetters.getOtherSalariesAndBonusesDetails(vacancy)
    }
    return secondTitleDescription
  }

  /**
   * Create the 'Wages' Card used in the Vacancy Details Page
   * @param IWage
   * @todo Break this apart into teeny tiny bits
   * @returns a formatted IVacancyDetailCard
   */
  static wages(localization: LocalizationData, wages: IWage[], vacancy: IVacancy): IVacancyDetailCard {
    const subcards: IVacancyDetailCardSubcard[] = []
    const title = VacancyDetailCardTitle.WagesAndShifts(localization)
    for (let i = 0; i < wages.length; i++) {
      let counter = 1 + i
      let subtitle = ''
      if (wages.length > 1)
        subtitle = LocalizationService.t<string>(localization, LocalizationPath.WageAndSalary, [counter.toString()])
      const subcard: IVacancyDetailCardSubcard = { title: subtitle, rowItemsArray: [] }
      subcard.rowItemsArray.push()

      const salary = VacancyDetailCardFactory.newSalarySubcard(localization, wages[i], vacancy.otherSalariesAndBonuses!)
      if (salary) subcard.rowItemsArray.push(salary)

      const shiftsSubcard = VacancyDetailCardFactory.newShiftSubcard(localization, wages[i])
      if (shiftsSubcard) subcard.rowItemsArray.push(shiftsSubcard)

      const salaryClosingDaySubcard = VacancyDetailCardFactory.newSalaryClosingDaySubcard(localization, wages[i])
      if (salaryClosingDaySubcard) subcard.rowItemsArray.push(salaryClosingDaySubcard)

      const workingDaysSubcard = VacancyDetailCardFactory.newWorkingDaysSubcard(localization, wages[i])
      if (workingDaysSubcard) subcard.rowItemsArray.push(workingDaysSubcard)

      const possibleAmountOfOvertimeSubcard = VacancyDetailCardFactory.newPossibleAmountOfOvertimeSubcard(localization, wages[i])
      if (possibleAmountOfOvertimeSubcard) subcard.rowItemsArray.push(possibleAmountOfOvertimeSubcard)

      const salaryPaymentDaySubcard = VacancyDetailCardFactory.newSalaryPaymentDaySubcard(localization, wages[i])
      if (salaryPaymentDaySubcard) subcard.rowItemsArray.push(salaryPaymentDaySubcard)

      if (wages.length > 1) {
        for (let rowitem of subcard.rowItemsArray) {
          if (rowitem.labels) {
            for (let label of rowitem.labels) label.class = 'theme-shades-light1-white'
          }
        }
      }
      subcards.push(subcard)
    }
    let secondTitle = VacancyDetailCardFactory.newWagesIncentivesTitle(localization, vacancy)
    let secondTitleDescription = VacancyDetailCardFactory.newWagesIncentivesDescription(vacancy)
    return {
      title: title,
      wide: true,
      secondTitle,
      secondTitleDescription,
      subcards: VacancyDetailCardFactory.vacancyDetailCardFormat(localization, title, subcards)
    }
  }
  /**
   * Create the 'Raises and Bonuses' Card used in the Vacancy Details Page
   * @param IWage
   * @returns a formatted IVacancyDetailCard
   */
  static raisesAndBonuses(localization: LocalizationData, vacancy: IVacancy): IVacancyDetailCard {
    const title = VacancyDetailCardTitle.RaisesAndBonuses(localization)
    const subcard: IVacancyDetailCardSubcard = { rowItemsArray: [] }
    /**
     * Create array of raises and bonuses for 'untitled' row of 'Raises and Bonuses' card
     */
    const raisesAndBonuses = VacancyCardText.getRaisesAndBonuses(localization, vacancy)
    if (raisesAndBonuses)
      subcard.rowItemsArray.push({
        labels: raisesAndBonuses.map((e) => LabelFactory.new({ label: [e] }))
      })
    return {
      title: title,
      subcards: VacancyDetailCardFactory.vacancyDetailCardFormat(localization, title, [subcard])
    }
  }

  /**
   * Create the 'Raises and Bonuses' Card used in the Vacancy Details Page
   * @param IWage
   * @returns a formatted IVacancyDetailCard
   */
  static socialInsuranceAndBenefits(localization: LocalizationData, vacancy: IVacancy): IVacancyDetailCard {
    const title = VacancyDetailCardTitle.SocialInsuranceAndBenefits(localization)
    const subcard: IVacancyDetailCardSubcard = { rowItemsArray: [] }
    const benefits = vacancy.benefits
    const labels = benefits?.map((e) => LabelFactory.new({ label: [e.benefit!.i18n![0].name!] }))
    if (labels && labels.length > 0) subcard.rowItemsArray.push({ labels })
    return { title: title, subcards: VacancyDetailCardFactory.vacancyDetailCardFormat(localization, title, [subcard]) }
  }

  /**
   *
   * @param vacancy
   * @returns
   */
  static japaneseLanguageSkills(localization: LocalizationData, vacancy: IVacancy): IVacancyDetailCard {
    const title = VacancyDetailCardTitle.JapaneseLanguageSkills(localization)
    const subcard: IVacancyDetailCardSubcard = { rowItemsArray: [] }

    const speakingLabelText = VacancyGetters.getJapaneseConversationName(vacancy)
    if (!!speakingLabelText) {
      const speakingLabel = LabelFactory.jpLevelSmall(speakingLabelText, 'text-large theme-shades-light1-white')
      subcard.rowItemsArray.push({labels: [speakingLabel]})
    }
    const result: IVacancyDetailCard = {
      title: title,
      subcards: VacancyDetailCardFactory.vacancyDetailCardFormat(localization, title, [subcard])
    }
    const japaneseLanguageDetails = vacancy.i18n![0].japaneseLanguageDetails
    if (japaneseLanguageDetails) {
      result.secondTitle = LocalizationService.t<string>(localization, LocalizationPath.JapaneseSkillsAdditionalInformation)
      result.secondTitleDescription = japaneseLanguageDetails
    }
    return result
  }

  /**
   *
   * @param vacancy
   * @returns
   */
  // static japaneseBusinessSkills(vacancy: IVacancy): IVacancyDetailCard {
  //   const title = VacancyDetailCardTitle.JapaneseBusinessSkills
  //   const rowItemsArray: IVacancyDetailCardRowItem[] = []
  //   const speakingLabelText = VacancyGetters.getBusinessSkillsNames(vacancy)
  //   const labels = speakingLabelText?.map((e) => LabelFactory.new({ label: e }))
  //   if (labels && labels.length > 0) rowItemsArray.push({ labels })
  //   return { title: title, rowItemsArray: VacancyDetailCardFactory.vacancyDetailCardFormat(title, rowItemsArray) }
  // }

  /**
   *
   * @param vacancy
   * @returns
   */
  static desiredSkills(localization: LocalizationData, vacancy: IVacancy): IVacancyDetailCard {
    const title = VacancyDetailCardTitle.DesiredSkills(localization)
    const subcard: IVacancyDetailCardSubcard = { rowItemsArray: [] }

    const labels = vacancy.desiredSkills?.map((e) => {
      const name = e.skill?.i18n[0].name
      const years = e.years!
      const yearUnit =
        years > 1
          ? LocalizationService.t<string>(localization, LocalizationPath.Years)
          : LocalizationService.t<string>(localization, LocalizationPath.Year)
      if (!years) return LabelFactory.new({ label: [name!] })
      return LabelFactory.new({ label: [`${name} - ${years} ${yearUnit}`] }) // TODO: Years of experience
    })
    if (labels && labels.length > 0) subcard.rowItemsArray.push({ labels })
    return { title: title, subcards: VacancyDetailCardFactory.vacancyDetailCardFormat(localization, title, [subcard]) }
  }

  /**
   *
   * @param vacancy
   * @returns
   */
  static requiredSkills(localization: LocalizationData, vacancy: IVacancy): IVacancyDetailCard {
    const title = VacancyDetailCardTitle.RequiredSkills(localization)
    const subcard: IVacancyDetailCardSubcard = { rowItemsArray: [] }

    const labels = vacancy.requiredSkills?.map((e) => {
      const name = e.skill?.i18n[0].name
      const years = e.years!
      const yearUnit =
        years > 1
          ? LocalizationService.t<string>(localization, LocalizationPath.Years)
          : LocalizationService.t<string>(localization, LocalizationPath.Year)
      if (!years) return LabelFactory.new({ label: [name!] })
      return LabelFactory.new({ label: [`${name} - ${years} ${yearUnit}`] }) // TODO: Years of experience
    })
    if (labels && labels.length > 0) subcard.rowItemsArray.push({ labels })
    return { title: title, subcards: VacancyDetailCardFactory.vacancyDetailCardFormat(localization, title, [subcard]) }
  }

  /**
   * Collect Detail Cards together in Array
   * @param IWage
   * @returns Array
   */
  static getAll(localization: LocalizationData, vacancy: IVacancy): IVacancyDetailCard[] {
    let allVacancyDetailCards = []
    const wage = vacancy.wages!
    allVacancyDetailCards.push(this.wages(localization, wage, vacancy))
    allVacancyDetailCards.push(this.japaneseLanguageSkills(localization, vacancy))
    allVacancyDetailCards.push(this.requiredSkills(localization, vacancy))
    allVacancyDetailCards.push(this.contractDetails(localization, vacancy))
    allVacancyDetailCards.push(this.raisesAndBonuses(localization, vacancy))
    allVacancyDetailCards.push(this.socialInsuranceAndBenefits(localization, vacancy))
    // allVacancyDetailCards.push(this.japaneseBusinessSkills(vacancy))
    allVacancyDetailCards.push(this.desiredSkills(localization, vacancy))
    return allVacancyDetailCards
  }

  /**
   *
   * @param title
   * @param array
   * @returns
   */
  static vacancyDetailCardFormat(
    localization: LocalizationData,
    title: string,
    subcards: IVacancyDetailCardSubcard[]
  ): IVacancyDetailCardSubcard[] {
    const lowercaseTitle = title.toLowerCase()

    for (const subcard of subcards) {
      if (subcard.rowItemsArray.length === 0)
        subcard.rowItemsArray.push({
          title: LocalizationService.t<string>(localization, LocalizationPath.NoListed, [lowercaseTitle])
        })
    }
    return subcards
  }

  /**
   * Create the 'Workplace Details' Card used in the Vacancy Details Page
   * @returns a formatted IVacancyDetailCard
   */
  static workplaceDetails(localization: LocalizationData, workplaceDetails: IWorkplaceDetails[]): IVacancyDetailCard {
    const title = VacancyDetailCardTitle.WorkplaceDetails(localization)
    const subcard: IVacancyDetailCardSubcard = { rowItemsArray: [] }
    const details = workplaceDetails
    const labels = details?.map((e) => LabelFactory.new({ label: [e.i18n![0].name!] }))
    if (labels && labels.length > 0) subcard.rowItemsArray.push({ labels })
    return { title: title, subcards: VacancyDetailCardFactory.vacancyDetailCardFormat(localization, title, [subcard]) }
  }
}
